import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getRoutesForRole, getNavigationRoutes } from '../configs/routes';
import { useUniversityConfig } from './UniversityConfigProvider';

interface NavigationContextType {
  isSideNav: boolean;
  setIsSideNav: React.Dispatch<React.SetStateAction<boolean>>;
  mobileOpen: boolean;
  setMobileOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  authorizedRoutes: any[];
  navigationRoutes: any[];
  role: string;
  userStatus: string | string[];
}

const defaultContextValues: NavigationContextType = {
  isSideNav: false,
  setIsSideNav: () => {},
  mobileOpen: false,
  setMobileOpen: () => {},
  isCollapsed: false,
  setIsCollapsed: () => {},
  authorizedRoutes: [],
  navigationRoutes: [],
  role: 'student',
  userStatus: 'ACTIVE'
};

const NavigationContext = createContext<NavigationContextType>(defaultContextValues);

interface NavigationProviderProps {
  children: React.ReactNode;
  user?: any;
  routes?: any[];
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children, user, routes }) => {
  const { config } = useUniversityConfig();
  
  const currentRole = user?.role;
  const currentStatus = user?.enrollmentStatus?.toUpperCase();
  const currentBatch = user?.batchName;
  const partner = user?.partner;
  
  const [isSideNav, setIsSideNav] = useState<boolean>(() => {
    try {
      const savedMode = localStorage.getItem('navigationMode');
      const defaultMode = config?.navigation?.defaultMode || 'side';
      return savedMode === 'true' || savedMode === 'side' || (savedMode === null && defaultMode === 'side');
    } catch (error) {
      return config?.navigation?.defaultMode === 'side' || false;
    }
  });

  const [mobileOpen, setMobileOpen] = useState<boolean>(false);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      const savedCollapsed = localStorage.getItem('sidebarCollapsed');
      return savedCollapsed === 'true';
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return false;
    }
  });

  const authorizedRoutes = useMemo(() => {
    if (routes) return routes;
    if (config) {
      return getRoutesForRole(currentRole, config, currentStatus, currentBatch, partner);
    }
    return [];
  }, [currentRole, currentStatus, currentBatch, partner, routes, config]);

  const navigationRoutes = useMemo(() => {
    return getNavigationRoutes(authorizedRoutes);
  }, [authorizedRoutes]);

  useEffect(() => {
    if (config?.navigation?.defaultMode && !localStorage.getItem('navigationMode')) {
      setIsSideNav(config.navigation.defaultMode === 'side');
    }
  }, [config]);

  useEffect(() => {
    try {
      localStorage.setItem('navigationMode', isSideNav ? 'side' : 'top');
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [isSideNav]);

  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [isCollapsed]);

  const contextValue = useMemo(
    () => ({
      isSideNav,
      setIsSideNav,
      mobileOpen,
      setMobileOpen,
      isCollapsed,
      setIsCollapsed,
      authorizedRoutes,
      navigationRoutes,
      role: currentRole,
      userStatus: currentStatus
    }),
    [isSideNav, mobileOpen, isCollapsed, authorizedRoutes, navigationRoutes, currentRole, currentStatus]
  );

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};

export const useNavigation = () => useContext(NavigationContext);
